import { Box, Typography } from '@mui/material'

import type { IconProps } from '@/assets/icons'
import {
  BasementIcon,
  BasementStairwayIcon,
  FireIcon,
  HeatingIcon,
  RoofIcon,
  ShowerIcon,
  VentilationIcon,
  WallsIcon,
  WindowsIcon,
} from '@/assets/icons'
import type {
  ComponentWithoutOrientation,
  CurrentStateDescription,
  Substance,
} from '@/shared/types/isfp.types'

enum BuildingComponent {
  ROOF = 'Dach / oberer Gebäudeabschluss',
  WALLS = 'Wände',
  WINDOWS = 'Fenster',
  BASEMENT = 'Keller / unterer Gebäudeabsschluss',
  BASEMENT_STAIRWAY = 'Kellerabgang',
}

const buildingComponentIcons: Record<
  BuildingComponent,
  {
    component: React.ComponentType<IconProps>
    width: number
    height: number
  }
> = {
  [BuildingComponent.ROOF]: { component: RoofIcon, width: 40, height: 40 },
  [BuildingComponent.WALLS]: { component: WallsIcon, width: 40, height: 40 },
  [BuildingComponent.WINDOWS]: { component: WindowsIcon, width: 32, height: 32 },
  [BuildingComponent.BASEMENT]: { component: BasementIcon, width: 40, height: 40 },
  [BuildingComponent.BASEMENT_STAIRWAY]: { component: BasementStairwayIcon, width: 40, height: 40 },
}

const buildingComponentDescriptionKeys: Record<BuildingComponent, keyof CurrentStateDescription> = {
  [BuildingComponent.ROOF]: 'upperCompletion',
  [BuildingComponent.WALLS]: 'exteriorWalls',
  [BuildingComponent.WINDOWS]: 'windows',
  [BuildingComponent.BASEMENT]: 'lowerCompletion',
  [BuildingComponent.BASEMENT_STAIRWAY]: 'basementEntrance',
}

enum SystemTechnology {
  HEATING = 'Heizung',
  HEAT_DISTRIBUTION = 'Wärmeverteilung',
  HOT_WATER = 'Warmwasser',
  VENTILATION = 'Lüftung',
}

const systemTechnologyIcons: Record<
  SystemTechnology,
  {
    component: React.ComponentType<IconProps>
    width: number
    height: number
  }
> = {
  [SystemTechnology.HEATING]: { component: FireIcon, width: 32, height: 32 },
  [SystemTechnology.HEAT_DISTRIBUTION]: { component: HeatingIcon, width: 32, height: 32 },
  [SystemTechnology.HOT_WATER]: { component: ShowerIcon, width: 32, height: 32 },
  [SystemTechnology.VENTILATION]: { component: VentilationIcon, width: 32, height: 32 },
}

const systemTechnologyDescriptionKeys: Record<SystemTechnology, keyof CurrentStateDescription> = {
  [SystemTechnology.HEATING]: 'heating',
  [SystemTechnology.HEAT_DISTRIBUTION]: 'heatingTransfer',
  [SystemTechnology.HOT_WATER]: 'drinkingHotWater',
  [SystemTechnology.VENTILATION]: 'ventilation',
}

const germanNumberFormatter = new Intl.NumberFormat('de-DE')

const renderDescriptionWithLineBreaks = (description: string) => {
  const lines = description.split('<br>')
  return lines.map((line, idx) => (
    <span key={idx}>
      {line}
      {idx < lines.length - 1 && <br />}
    </span>
  ))
}

const calculateUValue = <T extends ComponentWithoutOrientation>(
  component: T[]
): number | [number, number] | undefined => {
  if (component.length === 0) return undefined

  const uValues = component
    .map(comp => comp.uValue)
    .filter((uValue): uValue is number => uValue !== undefined)

  const uniqueUValues = Array.from(new Set(uValues))

  if (uniqueUValues.length === 0) return undefined
  if (uniqueUValues.length === 1) return uniqueUValues[0]
  return [Math.min(...uniqueUValues), Math.max(...uniqueUValues)]
}

interface CurrentStateSectionProps {
  substance?: Substance
  currentStateDescription?: CurrentStateDescription
}

const CurrentStateSection = ({ substance, currentStateDescription }: CurrentStateSectionProps) => {
  const uValuesAvailable: Record<BuildingComponent, number | [number, number] | undefined> = {
    [BuildingComponent.ROOF]: calculateUValue([
      ...(substance?.roofComponents || []),
      ...(substance?.topFloorCeilingComponents || []),
    ]),
    [BuildingComponent.WALLS]: calculateUValue([
      ...(substance?.exteriorWallComponents || []),
      ...(substance?.basementExteriorWallComponents || []),
    ]),
    [BuildingComponent.WINDOWS]: calculateUValue([
      ...(substance?.windowComponents || []),
      ...(substance?.doorComponents || []),
      ...(substance?.roofWindowComponents || []),
    ]),
    [BuildingComponent.BASEMENT]: calculateUValue([
      ...(substance?.basementCeilingComponents || []),
      ...(substance?.floorPlateComponents || []),
    ]),
    [BuildingComponent.BASEMENT_STAIRWAY]: undefined,
  }

  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '16px',
          marginBottom: '24px',
        }}
      >
        Ist Zustand:
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '24px',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            Bauteile
          </Typography>
          <Box>
            {Object.values(BuildingComponent).map((component, i) => {
              const iconConfig = buildingComponentIcons[component]
              const IconComponent = iconConfig.component
              const descriptionKey = buildingComponentDescriptionKeys[component]
              const description = currentStateDescription?.[descriptionKey]
              const uValue = uValuesAvailable[component]
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '16px',
                    marginBottom: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      backgroundColor: '#FAFAFB',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <IconComponent
                      width={iconConfig.width}
                      height={iconConfig.height}
                      style={uValue ? { position: 'absolute', top: '2px' } : undefined}
                    />

                    {uValue && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          width: '100%',
                          height: '25px',
                          backgroundColor: '#181D29',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '9px',
                            fontWeight: 500,
                            color: '#FFFFFF',
                            lineHeight: '10px',
                          }}
                        >
                          U-Wert:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '9px',
                            fontWeight: 500,
                            color: '#FFFFFF',
                            lineHeight: '10px',
                          }}
                        >
                          {Array.isArray(uValue)
                            ? `${germanNumberFormatter.format(uValue[0])} – ${germanNumberFormatter.format(uValue[1])}`
                            : germanNumberFormatter.format(uValue)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D29',
                      }}
                    >
                      {component}
                    </Typography>
                    {description && (
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '12px',
                          color: '#464A54',
                        }}
                      >
                        {renderDescriptionWithLineBreaks(description)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '8px',
            }}
          >
            Anlagentechnik
          </Typography>
          <Box>
            {Object.values(SystemTechnology).map((technology, i) => {
              const iconConfig = systemTechnologyIcons[technology]
              const IconComponent = iconConfig.component
              const descriptionKey = systemTechnologyDescriptionKeys[technology]
              const description = currentStateDescription?.[descriptionKey]
              const shouldFillColor =
                technology === SystemTechnology.HOT_WATER ||
                technology === SystemTechnology.HEAT_DISTRIBUTION
              return (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginTop: '16px',
                    marginBottom: '8px',
                  }}
                >
                  <Box
                    sx={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '12px',
                      backgroundColor: '#FAFAFB',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '& path': {
                        stroke: '#464A54',
                        ...(shouldFillColor && { fill: '#464A54' }),
                      },
                    }}
                  >
                    <IconComponent width={iconConfig.width} height={iconConfig.height} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '14px',
                        color: '#181D29',
                      }}
                    >
                      {technology}
                    </Typography>
                    {description && (
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '12px',
                          color: '#464A54',
                        }}
                      >
                        {renderDescriptionWithLineBreaks(description)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default CurrentStateSection
